/**
 * Coalesces sleep/wake signals (visibility, focus, online, pageshow) into a single
 * debounced recovery flush so multiple browser events do not stack duplicate work.
 */

export type WakeSignal =
  | "visibility_visible"
  | "focus"
  | "online"
  | "pageshow";

export type WakeRecoveryContext = {
  /** Deduped signals for this flush cycle, stable order */
  signals: WakeSignal[];
  /** Monotonic id for correlating logs */
  cycleId: number;
};

export type WakeRecoveryJob = (ctx: WakeRecoveryContext) => void;

export type WakeRecoveryLogger = {
  debug: (data: Record<string, unknown>, message: string) => void;
};

const NO_OP_LOGGER: WakeRecoveryLogger = {
  debug: () => {},
};

let logger: WakeRecoveryLogger = NO_OP_LOGGER;

/** Default debounce after the last signal before running registered jobs (ms). */
const DEFAULT_DEBOUNCE_MS = 550;

/** Stable execution order for known jobs; unknown keys run after, sorted. */
const JOB_ORDER: readonly string[] = [
  "network-status",
  "connection-status-distraction",
  "web-timer-resume",
  "desktop-foreground-session",
  "realtime-resume",
  "chat-resume",
  "tasks-resume",
];

const jobs = new Map<string, WakeRecoveryJob>();

let debounceMs = DEFAULT_DEBOUNCE_MS;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSignals: WakeSignal[] = [];
let cycleSeq = 0;
let isFlushing = false;
let flushAgainAfterCurrent = false;

// ---------------------------------------------------------------------------
// Real-wake evidence (TX-5255)
//
// A window `focus` is not always a wake. Closing a break video hands focus from
// its YouTube iframe back to the window: the page was never hidden and never
// left, yet every focus listener reported a wake and the whole recovery ran —
// sync, distraction, tasks, both channel lists — on each dialog close. Sentry's
// "Session long task" T2 and T3 both show that burst.
//
// The signal names cannot tell the two apart (TimerProvider labels a plain
// focus `visibility_visible` too), so the gate watches the page itself: when
// the window loses focus while the document still has it, focus only moved
// into one of the page's own frames. The `focus` that ends such a bounce is
// not a wake unless the tab was hidden in between, or too much time passed.
//
// Time matters because leaving FROM an iframe leaves no trace: the window
// already blurred when focus went into the frame, so switching to another app
// or closing the lid with the tab visible fires no second blur and no
// visibilitychange. Only the clock tells a member who closed a video from one
// who walked away mid-video. `Date.now()` is wall-clock time, so a machine
// that slept with the tab visible also reads as a long gap.
// ---------------------------------------------------------------------------

/**
 * Longest blur-to-flush gap still treated as an in-page bounce.
 *
 * The bounces this exists for are seconds long: both Sentry events show the
 * overlay clicked 1–5s after focus went into the video. Two minutes covers a
 * member watching a short break video through, and no longer — skipping a real
 * wake leaves stale data, while running a needless recovery costs a handful of
 * requests, so the bound errs short.
 */
const MAX_BOUNCE_MS = 2 * 60 * 1000;

/** Window blurred, but focus stayed inside this document (an in-page iframe). */
let focusStayedInPage = false;
/** The document was hidden at some point since the window last lost focus. */
let hiddenSinceBlur = false;
/** Wall-clock time of the last window blur. */
let blurAt = 0;
let evidenceListenersInstalled = false;

function installWakeEvidenceListeners(): void {
  if (evidenceListenersInstalled) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  evidenceListenersInstalled = true;
  window.addEventListener("blur", () => {
    hiddenSinceBlur = false;
    focusStayedInPage = false;
    blurAt = Date.now();
    // `document.activeElement` / `hasFocus()` settle after the blur dispatch.
    setTimeout(() => {
      focusStayedInPage =
        document.visibilityState === "visible" && document.hasFocus();
    }, 0);
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") hiddenSinceBlur = true;
  });
}

const BOUNCE_SIGNALS: ReadonlySet<WakeSignal> = new Set(["focus", "visibility_visible"]);

/** True when the pending cycle is only a focus returning from an in-page frame. */
function isInPageFocusBounce(signals: readonly WakeSignal[]): boolean {
  return (
    focusStayedInPage &&
    !hiddenSinceBlur &&
    Date.now() - blurAt <= MAX_BOUNCE_MS &&
    signals.every((signal) => BOUNCE_SIGNALS.has(signal))
  );
}

// ---------------------------------------------------------------------------
// Metrics (in-memory; dev / debugging)
// ---------------------------------------------------------------------------

export type WakeRecoveryCycleRecord = {
  cycleId: number;
  signals: WakeSignal[];
  debounceMs: number;
  wallMs: number;
  handlerWallMs: Record<string, number>;
  notifyCountBeforeFlush: number;
};

const MAX_CYCLE_HISTORY = 12;
const cycleHistory: WakeRecoveryCycleRecord[] = [];

let totalNotifyCalls = 0;
let totalFlushes = 0;

function pushCycleRecord(rec: WakeRecoveryCycleRecord): void {
  cycleHistory.push(rec);
  if (cycleHistory.length > MAX_CYCLE_HISTORY) {
    cycleHistory.shift();
  }
}

export function getWakeRecoveryMetricsSnapshot(): {
  totalNotifyCalls: number;
  totalFlushes: number;
  recentCycles: WakeRecoveryCycleRecord[];
} {
  return {
    totalNotifyCalls,
    totalFlushes,
    recentCycles: [...cycleHistory],
  };
}

/** Test helper */
export function resetWakeRecoveryMetricsForTests(): void {
  totalNotifyCalls = 0;
  totalFlushes = 0;
  cycleHistory.length = 0;
  focusStayedInPage = false;
  hiddenSinceBlur = false;
  blurAt = 0;
}

// ---------------------------------------------------------------------------

export function setWakeRecoveryLogger(next: WakeRecoveryLogger | null): void {
  logger = next ?? NO_OP_LOGGER;
}

export function setWakeRecoveryDebounceMs(ms: number): void {
  debounceMs = Math.max(0, ms);
}

export function registerWakeRecoveryJob(
  key: string,
  job: WakeRecoveryJob,
): void {
  installWakeEvidenceListeners();
  jobs.set(key, job);
}

export function unregisterWakeRecoveryJob(key: string): void {
  jobs.delete(key);
}

function dedupeSignalOrder(signals: WakeSignal[]): WakeSignal[] {
  const seen = new Set<WakeSignal>();
  const out: WakeSignal[] = [];
  for (const s of signals) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function orderedJobKeys(): string[] {
  const known: string[] = [];
  for (const k of JOB_ORDER) {
    if (jobs.has(k)) known.push(k);
  }
  const rest = [...jobs.keys()]
    .filter((k) => !JOB_ORDER.includes(k))
    .sort();
  return [...known, ...rest];
}

/**
 * Hands the main thread back between wake jobs.
 *
 * `scheduler.yield()` resumes the continuation at a higher priority than a
 * timer would, so recovery does not queue up behind whatever else the page
 * scheduled while it was hidden. `setTimeout` is the fallback wherever it is
 * missing — every non-Chromium browser today, and jsdom.
 */
function yieldToMainThread(): Promise<void> {
  const { scheduler } = globalThis as {
    scheduler?: { yield?: () => Promise<void> };
  };
  if (typeof scheduler?.yield === "function") {
    // A yield can reject when the task is aborted mid-navigation. Swallow it:
    // an unresolved yield would strand `isFlushing` and silently kill every
    // wake recovery for the life of the page.
    return scheduler.yield().catch(() => {});
  }
  return new Promise((resolve) => {
    if (typeof setTimeout === "undefined") {
      resolve();
      return;
    }
    setTimeout(resolve, 0);
  });
}

function scheduleFlush(): void {
  if (typeof setTimeout === "undefined") {
    void flushWakeRecoveryNow();
    return;
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushWakeRecoveryNow();
  }, debounceMs);
}

/**
 * Bumps the wake recovery debouncer. Multiple rapid calls (focus + visibility + online)
 * collapse into one flush after `debounceMs` quiet period.
 */
export function notifyWakeSignals(
  signal: WakeSignal | WakeSignal[],
): void {
  const batch = Array.isArray(signal) ? signal : [signal];
  pendingSignals.push(...batch);
  totalNotifyCalls += 1;

  logger.debug(
    {
      signals: batch,
      pendingLength: pendingSignals.length,
    },
    "[wakeRecovery] notifyWakeSignals",
  );

  if (isFlushing) {
    flushAgainAfterCurrent = true;
    return;
  }
  scheduleFlush();
}

/**
 * Runs one job per task, yielding in between.
 *
 * Running all seven back to back is what produced THE-SUKHA-COMPANY-RM's
 * post-wake variant: 942ms inside a single `user-callback
 * TimerHandler:setTimeout`. Every job fans out into `invalidateQueries`, so the
 * refetches and the re-renders they queue all landed in one task the browser
 * could not interrupt to paint. Order is still strictly sequential — the jobs
 * depend on it — but the frame is no longer indivisible.
 */
async function flushWakeRecoveryNow(): Promise<void> {
  if (pendingSignals.length === 0) {
    return;
  }

  const signals = dedupeSignalOrder(pendingSignals);
  pendingSignals = [];

  const bounce = isInPageFocusBounce(signals);
  // Only a focus return ends the bounce, so only a cycle made of focus signals
  // spends its evidence; the next blur re-establishes it. A `pageshow` or
  // `online` cycle that flushes while focus is still in the frame (the page-load
  // `pageshow` lands 550ms late) must leave it for the close that follows.
  if (signals.every((signal) => BOUNCE_SIGNALS.has(signal))) {
    focusStayedInPage = false;
  }
  if (bounce) {
    logger.debug({ signals }, "[wakeRecovery] skipped: focus returned from an in-page frame");
    return;
  }

  isFlushing = true;
  const notifyBatchSize = signals.length;
  const cycleId = ++cycleSeq;
  totalFlushes += 1;

  const wallStart = typeof performance !== "undefined" ? performance.now() : 0;
  const handlerWallMs: Record<string, number> = {};

  logger.debug(
    {
      cycleId,
      signals,
      jobCount: jobs.size,
      debounceMs,
      notifyBatchSize,
    },
    "[wakeRecovery] flush start",
  );

  const keys = orderedJobKeys();
  try {
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      // Re-read rather than snapshot: a provider can unmount across a yield, and
      // running its job after that would touch a dead query client.
      const job = jobs.get(key);
      if (job) {
        const h0 = typeof performance !== "undefined" ? performance.now() : 0;
        try {
          job({ signals, cycleId });
        } catch (error) {
          logger.debug(
            {
              cycleId,
              key,
              error: error instanceof Error ? error.message : String(error),
            },
            "[wakeRecovery] job threw",
          );
        }
        if (typeof performance !== "undefined") {
          handlerWallMs[key] = Math.round(performance.now() - h0);
        }
      }
      if (index < keys.length - 1) {
        await yieldToMainThread();
      }
    }

    // Wall clock across the whole cycle, yields included. Per-job cost is in
    // `handlerWallMs` — that is the number to read when hunting a long frame.
    const wallMs =
      typeof performance !== "undefined"
        ? Math.round(performance.now() - wallStart)
        : 0;

    pushCycleRecord({
      cycleId,
      signals,
      debounceMs,
      wallMs,
      handlerWallMs,
      notifyCountBeforeFlush: notifyBatchSize,
    });

    logger.debug(
      {
        cycleId,
        signals,
        wallMs,
        handlerWallMs,
      },
      "[wakeRecovery] flush complete",
    );
  } finally {
    // Never leave this set: a stranded `isFlushing` makes `notifyWakeSignals`
    // swallow every later wake instead of scheduling one.
    isFlushing = false;
  }

  if (flushAgainAfterCurrent || pendingSignals.length > 0) {
    flushAgainAfterCurrent = false;
    if (pendingSignals.length > 0) {
      scheduleFlush();
    }
  }
}
